/**
 * EcosystemGraph Component
 * Full interactive force-directed graph of the entire Colorado STEM Ecosystem,
 * rendered on the Directory page. Every organization in the database is a node;
 * edges represent Neo4j "CONNECTED_TO" relationships between organizations.
 *
 * Graph structure:
 * - Nodes: Organizations rendered at a uniform size and colored by sector or
 *   region (toggled by the user).
 * - Links: Undirected organization-to-organization relationships stored in
 *   Neo4j and returned by GET /api/graph/full.
 *
 * Force simulation (d3-force):
 * - charge (forceManyBody): Connected nodes repel at -50; unconnected nodes
 *   repel weakly at -10 so they cluster instead of flying outward.
 * - radial (forceRadial): Pulls unconnected nodes toward radius 150 from
 *   the origin (strength 0.3) while connected nodes are unaffected (strength 0).
 *   This keeps isolated nodes in a tidy ring rather than scattered.
 * - The default center and link forces from react-force-graph-2d are left
 *   at their defaults (center at [0,0], link distance ~30).
 *
 * Key interactions:
 * - Click a node to navigate to that organization's detail page
 * - Hover a node to highlight it with a gold ring and show its label
 * - Search filters the visible nodes/links client-side
 * - Color-by toggle switches between sector and region palettes
 * - Zoom in/out/reset controls delegate to ForceGraph2D's zoom API
 * - On small screens, falls back to GraphMobileView (stats + list)
 *
 * A "?" explainer (GraphExplainer, #27) sits in the top-right corner
 * explaining what a dot/line means to a first-time visitor with no prior
 * context for the visualization.
 */

import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import ForceGraph2D from 'react-force-graph-2d';
import { forceManyBody, forceRadial, forceCollide } from 'd3-force-3d';
import { Card, Spinner, Alert } from 'react-bootstrap';

import { useEcosystem } from '@/shared/contexts/EcosystemContext';

import { useGraphData } from '../hooks/useGraphData';
import { useGraphFilters } from '../hooks/useGraphFilters';
import { getNodeColor, buildRegionColors } from '../utils/graphColors';
import {
    filterGraphBySearch,
    filterByMinConnections,
    getHighlightedNodes,
    getNodeSize,
} from '../utils/graphHelpers';

import GraphControls from './GraphControls';
import GraphExplainer from './GraphExplainer';
import GraphLegend from './GraphLegend';
import GraphMobileView from './GraphMobileView';

import '../Graph.css';

export default function EcosystemGraph({
    initialFilters = {},
    height = 600,
    showControls = true,
    showLegend = true,
    onNodeClick,
}) {
    const navigate = useNavigate();
    const { ecosystem } = useEcosystem();
    const graphRef = useRef();
    const containerRef = useRef();
    const [dimensions, setDimensions] = useState({ width: 800, height });
    const [isMobile, setIsMobile] = useState(false);
    const [hoveredNode, setHoveredNode] = useState(null);

    // Dynamic region color map sourced from the active tenant's taxonomy:
    // region names are tenant-specific, so there is no fixed palette to fall
    // back on.
    const regionColors = useMemo(
        () => buildRegionColors(ecosystem?.taxonomy?.regions),
        [ecosystem?.taxonomy?.regions]
    );

    // Filter state
    const { filters, setFilter, hasActiveFilters, apiFilters } =
        useGraphFilters(initialFilters);

    // Fetch graph data with API filters
    const { graphData, loading, error, refetch } = useGraphData(apiFilters);

    // --- Data transformation pipeline ---
    // The API returns the full ecosystem graph (all organizations + edges).
    // Client-side filters are applied in sequence to produce the set of
    // nodes and links actually rendered by ForceGraph2D.
    const displayedData = useMemo(() => {
        let data = graphData;

        // Filter 1: Remove organizations with fewer connections than the threshold.
        // This lets users cut visual noise by hiding low-connectivity nodes.
        if (filters.minConnections) {
            data = filterByMinConnections(data, filters.minConnections);
        }

        // Filter 2: Keep only nodes whose name matches the search query.
        // Links between non-matching nodes are also removed.
        if (filters.searchTerm) {
            data = filterGraphBySearch(data, filters.searchTerm);
        }

        return data;
    }, [graphData, filters.searchTerm, filters.minConnections]);

    // Get highlighted nodes for search
    const highlightedNodes = useMemo(() => {
        return getHighlightedNodes(graphData.nodes, filters.searchTerm);
    }, [graphData.nodes, filters.searchTerm]);

    // Build a set of node IDs that participate in at least one link.
    // Used by the force configuration below to apply different physics
    // to connected vs. isolated (unconnected) nodes.
    // Note: link.source can be a string (before simulation) or an object
    // (after d3-force resolves it), so we check for both forms.
    const connectedNodeIds = useMemo(() => {
        const ids = new Set();
        displayedData.links.forEach((link) => {
            const sourceId =
                typeof link.source === 'object' ? link.source.id : link.source;
            const targetId =
                typeof link.target === 'object' ? link.target.id : link.target;
            ids.add(sourceId);
            ids.add(targetId);
        });
        return ids;
    }, [displayedData.links]);

    // --- Force simulation configuration ---
    // Override the default d3-force charge and add a radial force so that
    // isolated (unconnected) nodes form a compact cluster rather than
    // drifting to the edges of the canvas.
    useEffect(() => {
        if (graphRef.current) {
            // Charge force (forceManyBody): controls node-to-node repulsion.
            // Connected nodes get -50 (moderate repulsion) so the network
            // spreads out enough to see structure. Unconnected nodes get
            // only -10 (weak repulsion) so they stay compact.
            graphRef.current.d3Force(
                'charge',
                forceManyBody().strength((node) => {
                    return connectedNodeIds.has(node.id) ? -50 : -10;
                })
            );

            // Radial force (forceRadial): pulls nodes toward a circle of
            // radius 150 centered at [0,0]. Connected nodes have strength 0
            // (unaffected), while unconnected nodes are pulled inward at
            // strength 0.3 to form a visible cluster near the graph center.
            graphRef.current.d3Force(
                'radial',
                forceRadial(150, 0, 0).strength((node) =>
                    connectedNodeIds.has(node.id) ? 0 : 0.3
                )
            );

            // Collision force (forceCollide): treats the nodes as having
            // a physical volume. Without this the simulation has no concept
            // of nodes touching and they are able to slide on top of each other
            // freely.
            graphRef.current.d3Force('collision', forceCollide(8));

            // Reheat the simulation so the new force parameters take effect
            // (resets the simulation's cooling alpha back toward 1).
            graphRef.current.d3ReheatSimulation();
        }
    }, [connectedNodeIds]);

    // Handle responsive sizing
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                setDimensions({ width, height });
            }
            setIsMobile(window.innerWidth < 992);
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [height]);

    // Handle node click
    const handleNodeClick = useCallback(
        (node) => {
            if (onNodeClick) {
                onNodeClick(node);
            } else {
                navigate(`/organizations/${node.id}`);
            }
        },
        [navigate, onNodeClick]
    );

    // Handle filter changes from controls
    const handleFilterChange = useCallback(
        (key, value) => {
            setFilter(key, value);
        },
        [setFilter]
    );

    // --- Custom node rendering (Canvas 2D) ---
    // Called once per node per frame by react-force-graph-2d. We draw each
    // node ourselves rather than using the default renderer so we can
    // control sizing, coloring, highlight rings, and label visibility.
    const nodeCanvasObject = useCallback(
        (node, ctx, globalScale) => {
            // Uniform size from getNodeSize() - no organization appears more prominent than another.
            const size = getNodeSize();

            // Color determined by the user's colorBy toggle (sector or region).
            // Region map is tenant-specific (built from ecosystem.taxonomy.regions);
            // sectors fall through to SECTOR_COLORS in graphColors.js.
            const color = getNodeColor(node, filters.colorBy, regionColors);
            const isHighlighted = highlightedNodes.has(node.id);
            const isHovered = hoveredNode === node.id;

            // Draw filled circle for the node
            ctx.beginPath();
            ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();

            // Draw a colored ring around the node when highlighted or hovered.
            // Hover uses gold (#FFD700); search-match uses coral (#FF6B6B).
            if (isHighlighted || isHovered) {
                ctx.strokeStyle = isHovered ? '#FFD700' : '#FF6B6B';
                ctx.lineWidth = 2;
                ctx.stroke();
            }

            // Draw the organization name label below the node.
            // Labels only appear when the user has zoomed in past 1.5x or
            // is hovering the node, keeping the default zoom level uncluttered.
            if (globalScale > 1.5 || isHovered) {
                const label = node.name;
                // Font size shrinks as the user zooms out (inversely proportional
                // to globalScale), but never below 3px to stay readable.
                const fontSize = Math.max(10 / globalScale, 3);
                ctx.font = `${fontSize}px Sans-Serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = '#333';
                ctx.fillText(label, node.x, node.y + size + fontSize);
            }
        },
        [filters.colorBy, highlightedNodes, hoveredNode, regionColors]
    );

    // Node hover handling
    const handleNodeHover = useCallback((node) => {
        setHoveredNode(node ? node.id : null);
    }, []);

    // --- Zoom controls ---
    // These use relative multipliers so they can be invoked repeatedly.
    // The second argument (200ms) is the animation duration.
    const handleZoomIn = useCallback(() => {
        if (graphRef.current) {
            const currentZoom = graphRef.current.zoom();
            graphRef.current.zoom(currentZoom * 1.5, 200); // 1.5x closer
        }
    }, []);

    const handleZoomOut = useCallback(() => {
        if (graphRef.current) {
            const currentZoom = graphRef.current.zoom();
            graphRef.current.zoom(currentZoom * 0.67, 200); // ~0.67x further (inverse of 1.5)
        }
    }, []);

    // Reset: fit all nodes into view with 50px padding, animated over 400ms
    const handleResetZoom = useCallback(() => {
        if (graphRef.current) {
            graphRef.current.zoomToFit(400, 50);
        }
    }, []);

    // Show mobile view on small screens
    if (isMobile) {
        return <GraphMobileView />;
    }

    // Loading state
    if (loading) {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-5 text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">
                        Loading ecosystem graph...
                    </p>
                </Card.Body>
            </Card>
        );
    }

    // Error state
    if (error) {
        return (
            <Alert variant="danger">
                <Alert.Heading>Unable to load graph</Alert.Heading>
                <p>{error}</p>
                <button className="btn btn-outline-danger" onClick={refetch}>
                    Try Again
                </button>
            </Alert>
        );
    }

    return (
        <Card className="border-0 shadow-sm ecosystem-graph-container">
            {showControls && (
                <Card.Header className="bg-white border-bottom">
                    <GraphControls
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onZoomIn={handleZoomIn}
                        onZoomOut={handleZoomOut}
                        onResetZoom={handleResetZoom}
                    />
                </Card.Header>
            )}

            <Card.Body className="p-0 position-relative" ref={containerRef}>
                {/* ForceGraph2D renders on a <canvas> element.
                    Key props explained:
                    - nodeCanvasObject: our custom node renderer (see above)
                    - nodePointerAreaPaint: invisible hit-test area slightly
                      larger (+2px) than the visible node so clicks/hovers
                      are easier to trigger
                    - linkColor/linkWidth: subtle gray lines between nodes
                    - cooldownTicks: simulation runs for 100 ticks then freezes
                    - onEngineStop: auto-fit all nodes into view once settled
                    - enableNodeDrag/Zoom/Pan: full interactivity enabled */}
                <ForceGraph2D
                    ref={graphRef}
                    graphData={displayedData}
                    width={dimensions.width}
                    height={dimensions.height}
                    nodeCanvasObject={nodeCanvasObject}
                    nodePointerAreaPaint={(node, color, ctx) => {
                        ctx.fillStyle = color;
                        ctx.beginPath();
                        ctx.arc(node.x, node.y, 8, 0, 2 * Math.PI);
                        ctx.fill();
                    }}
                    linkColor={() => '#E0E0E0'}
                    linkWidth={1}
                    onNodeClick={handleNodeClick}
                    onNodeHover={handleNodeHover}
                    cooldownTicks={100}
                    onEngineStop={() => {
                        if (graphRef.current) {
                            graphRef.current.zoomToFit(400, 50);
                        }
                    }}
                    enableNodeDrag={true}
                    enableZoomInteraction={true}
                    enablePanInteraction={true}
                />

                {showLegend && (
                    <div className="graph-legend-overlay">
                        <GraphLegend
                            colorBy={filters.colorBy}
                            regionColors={regionColors}
                        />
                    </div>
                )}

                {/* Node count indicator */}
                <div className="graph-stats-overlay">
                    <small className="text-muted">
                        {displayedData.nodes.length} organizations
                        {hasActiveFilters && ' (filtered)'}
                    </small>
                </div>

                {/* "How to read this map" explainer (#27) */}
                <div className="graph-explainer-overlay">
                    <GraphExplainer />
                </div>
            </Card.Body>
        </Card>
    );
}

EcosystemGraph.propTypes = {
    initialFilters: PropTypes.shape({
        sector: PropTypes.string,
        region: PropTypes.string,
        programming: PropTypes.string,
        searchTerm: PropTypes.string,
        colorBy: PropTypes.string,
    }),
    height: PropTypes.number,
    showControls: PropTypes.bool,
    showLegend: PropTypes.bool,
    onNodeClick: PropTypes.func,
};

EcosystemGraph.defaultProps = {
    initialFilters: {},
    height: 600,
    showControls: true,
    showLegend: true,
    onNodeClick: null,
};
